import type {
  BatteryProposalAddressDto,
  BatteryProposalDto,
  BatteryProposalPersonDto,
} from "~/api/dto/battery-proposal.dto"
import powurLogoWhite from "~/assets/svg/powur-logo-white.svg?url"

import { ProposalSetupFab } from "../../ProposalSetup/ProposalSetupFab"
import { isBatteryProposalReadOnly } from "../battery-proposal-read-only"
import { formatPersonPhone, personName } from "../person-display"
import type { BatteryPresentationProposal } from "../presentation-proposal"
import { Eyebrow, Icon } from "../primitives"
import { batterySetupMenuPath } from "../setup/setup-steps"
import { SECTION_LINKS } from "../static-copy"
import { useActiveSection } from "../use-active-section"

const personContact = (person: BatteryProposalPersonDto): string =>
  [person.email, formatPersonPhone(person.phone)].filter(Boolean).join(` · `)

const formatAddress = (address: BatteryProposalAddressDto): string =>
  [
    address.street,
    [address.city, `${address.state} ${address.postal}`.trim()]
      .filter(Boolean)
      .join(`, `),
  ]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(`, `)

const parseCreatedAt = (
  createdAt: string,
): { year: number; label: string } | null => {
  const created = new Date(createdAt)
  if (Number.isNaN(created.getTime())) {
    return null
  }

  return {
    year: created.getUTCFullYear(),
    label: new Intl.DateTimeFormat(`en-US`, {
      month: `long`,
      day: `numeric`,
      year: `numeric`,
      timeZone: `UTC`,
    }).format(created),
  }
}

/** Wordmark plus the people on the proposal, in one band. Deliberately not
 * sticky: the person columns are too tall to pin, and SectionRail carries
 * wayfinding for the whole scroll. */
export function BatteryHeader({
  proposal,
}: {
  proposal: BatteryPresentationProposal
}): JSX.Element {
  const { consultant, mentor } = proposal
  const homeowner = proposal.homeowner
    ? personName(proposal.homeowner, `Homeowner`)
    : `Homeowner`
  const address = proposal.address ? formatAddress(proposal.address) : ``

  return (
    <header className="border-b border-bp-border bg-bp-base px-6 py-5 lg:px-16">
      <div className="flex items-center gap-4">
        <img src={powurLogoWhite} alt="Powur" className="h-[26px] w-auto" />
        <div className="h-[22px] w-px bg-bp-border-strong" />
        <span className="text-[length:var(--step--2)] font-semibold tracking-[0.18em] text-bp-text-dim uppercase">
          Battery Proposal
        </span>
      </div>
      <div
        className={`mt-7 grid items-center gap-8 md:gap-12 ${
          mentor && consultant
            ? `md:grid-cols-3`
            : mentor || consultant
              ? `md:grid-cols-2`
              : ``
        }`}
      >
        <div className="min-w-0">
          <Eyebrow className="mb-1">Prepared for</Eyebrow>
          <div
            className="font-display truncate text-[length:var(--step-1)] leading-tight font-semibold tracking-[-0.015em] text-bp-text"
            title={homeowner}
          >
            {homeowner}
          </div>
          {address ? (
            <div className="mt-1 flex items-center gap-1.5 text-[length:var(--step--2)] text-bp-text-muted">
              <Icon name="pin" className="size-3 text-bp-text-dim" />
              <span className="truncate" title={address}>
                {address}
              </span>
            </div>
          ) : null}
        </div>
        {consultant ? (
          <PersonColumn
            eyebrow="Prepared by"
            person={consultant}
            fallbackName="your consultant"
          />
        ) : null}
        {mentor ? (
          <PersonColumn
            eyebrow="In partnership with"
            person={mentor}
            fallbackName="your mentor"
          />
        ) : null}
      </div>
    </header>
  )
}

/** Desktop-only dot rail pinned to the right edge: one dot per numbered
 * section, scroll-spy highlight, labels on hover/focus. */
export function SectionRail({
  showSavings,
  showPowurCare,
  showAiCompute,
  hidden = false,
  staleSections,
}: {
  showSavings: boolean
  showPowurCare: boolean
  showAiCompute: boolean
  /** Steps aside while the seller's Updates panel covers the right edge. */
  hidden?: boolean
  /** Sections whose figures are out of date: a gold badge on their dot. */
  staleSections?: ReadonlySet<string>
}): JSX.Element {
  // "Top" leads the rail so the hero stays reachable from any scroll depth;
  // it lives outside SECTION_LINKS because the hero isn't a numbered section.
  const links = [
    { id: `hero`, label: `Top` },
    ...SECTION_LINKS.filter(
      (link) =>
        (showSavings || link.id !== `savings`) &&
        (showPowurCare || link.id !== `powur-care`) &&
        (showAiCompute || link.id !== `ai-compute`),
    ),
  ]
  const active = useActiveSection(links.map((link) => link.id))

  return (
    <nav
      aria-label="Sections"
      className={`fixed top-1/2 right-5 z-20 hidden -translate-y-1/2 flex-col gap-2.5 transition-opacity lg:flex print:hidden ${
        hidden ? `pointer-events-none invisible opacity-0` : ``
      }`}
    >
      {links.map((link) => {
        const stale = staleSections?.has(link.id) === true
        return (
          <a
            key={link.id}
            href={`#${link.id}`}
            data-stale={stale || undefined}
            aria-current={active === link.id ? `location` : undefined}
            className="group flex items-center justify-end gap-2.5 focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none"
          >
            <span
              className={`rounded-full border border-bp-border bg-bp-page px-2.5 py-0.5 text-[length:var(--step--2)] font-semibold whitespace-nowrap opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 ${
                active === link.id ? `text-bp-text` : `text-bp-text-muted`
              }`}
            >
              {link.label}
              {stale ? ` · out of date` : null}
            </span>
            <span
              className={`relative size-2.5 shrink-0 rounded-full border transition-transform ${
                active === link.id
                  ? `scale-125 border-bp-accent bg-bp-accent`
                  : `border-bp-border-strong bg-bp-surface group-hover:border-bp-accent`
              }`}
            >
              {stale ? (
                <span
                  aria-hidden="true"
                  className="absolute -top-[5px] -right-[5px] size-[7px] rounded-full bg-bp-gold ring-2 ring-bp-page"
                />
              ) : null}
            </span>
          </a>
        )
      })}
    </nav>
  )
}

/** The floating Setup entry, for actors who may edit this proposal. */
export function SetupFab({
  proposal,
}: {
  proposal: BatteryProposalDto
}): JSX.Element | null {
  if (isBatteryProposalReadOnly(proposal)) {
    return null
  }

  return <ProposalSetupFab to={batterySetupMenuPath(proposal.id)} />
}

function PersonColumn({
  eyebrow,
  person,
  fallbackName,
}: {
  eyebrow: string
  person: BatteryProposalPersonDto
  fallbackName: string
}): JSX.Element {
  const name = personName(person, fallbackName)
  const line = personContact(person)

  return (
    <div className="min-w-0">
      <Eyebrow className="mb-1">{eyebrow}</Eyebrow>
      <div
        className="font-display truncate text-[length:var(--step-1)] leading-tight font-semibold tracking-[-0.015em] text-bp-text"
        title={name}
      >
        {name}
      </div>
      {line ? (
        <div
          className="mt-1 truncate text-[length:var(--step--2)] text-bp-text-muted"
          title={line}
        >
          {line}
        </div>
      ) : null}
    </div>
  )
}

export function BatteryFooter({
  proposal,
}: {
  proposal: BatteryPresentationProposal
}): JSX.Element {
  const created = parseCreatedAt(proposal.createdAt)
  const year = created?.year ?? new Date().getUTCFullYear()

  return (
    <footer className="flex flex-wrap items-center justify-between gap-4 border-t border-bp-border px-6 pt-8 pb-8 lg:px-16">
      <img src={powurLogoWhite} alt="Powur" className="h-7 w-auto" />
      <div className="text-[length:var(--step--2)] tracking-wide text-bp-text-dim">
        © {year} Powur · Battery Proposal
        {created ? ` · ${created.label}` : ``}
      </div>
    </footer>
  )
}
